import React from 'react';

/**
 * Catches a crash anywhere below it and shows what went wrong.
 *
 * Without this, a single render error blanks the whole page -- white screen,
 * no message, nothing to act on. That has cost real time on this app more
 * than once: a missing import took the app down and the only way to find it
 * was opening the browser's developer console.
 *
 * Now the error is on screen, in words, with a button to carry on.
 */
interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
  info: string;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null, info: '' };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Still logged, so the console has the full stack for anyone who wants it.
    console.error('[ScaleSupport] Screen error:', error, info);
    this.setState({ info: info?.componentStack || '' });
  }

  private reset = () => {
    this.setState({ error: null, info: '' });
  };

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    const details = `${error.name}: ${error.message}\n\n${info}`.trim();

    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4 font-sans">
        <div className="bg-white border border-slate-200 rounded-2xl shadow-xl max-w-2xl w-full overflow-y-auto max-h-[92vh]">
          <div className="px-6 py-4 bg-rose-600 text-white">
            <h1 className="text-sm font-bold">Something on this screen stopped working</h1>
            <p className="text-[11px] text-rose-50">
              Your data is safe. Nothing has been lost.
            </p>
          </div>

          <div className="p-6 space-y-4 text-xs text-slate-700">
            <p>
              Try going back to the dashboard. If it keeps happening, send the
              message below — it says exactly what went wrong.
            </p>

            <pre className="p-3 bg-slate-900 text-slate-100 rounded-xl overflow-x-auto text-[11px] whitespace-pre-wrap max-h-64">
              {details}
            </pre>

            <div className="flex flex-wrap gap-2 justify-end">
              <button
                type="button"
                onClick={() => {
                  try {
                    void navigator.clipboard.writeText(details);
                  } catch {
                    /* clipboard unavailable; the text is on screen */
                  }
                }}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold transition cursor-pointer"
              >
                Copy the message
              </button>
              <button
                type="button"
                onClick={this.reset}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-800 text-white rounded-xl font-bold transition cursor-pointer"
              >
                Back to the app
              </button>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition cursor-pointer"
              >
                Reload
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
